import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_FORM_SETTINGS } from '../../graphql';
import { UPDATE_TINYCMS_FORM_SETTINGS } from '../../graphql/mutations';
import { useAddons } from './addons';
import { InkSection } from './shared';
import { Loader } from '../common/PageLoader';
import { askInk } from './InkConfirm';

/**
 * Content → Forms → Form settings ("tinycms UX optimization review v7",
 * Prototype v3): how long responses are kept and what readers see under every
 * form. Responses stay in Forms — nothing is emailed. Account admins change
 * these; everyone else sees them. Small print is set by form type (v14): one
 * line for contact forms (readers write in, you reply), one for sign-up forms
 * (readers ask to be sent something); a form's own line wins.
 */

/**
 * One small-print line (design review round 27 §1): the text, its own Save
 * (disabled until it changes), and where it stands: Not saved, Saved or
 * Nothing shown.
 */
function SmallPrintRow({
  title,
  desc,
  placeholder,
  value,
  canManage,
  saving,
  onSave,
}: {
  title: string;
  desc: string;
  placeholder: string;
  value: string;
  canManage: boolean;
  saving: boolean;
  onSave: (text: string) => void;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const status = text !== value ? 'Not saved' : text.trim() ? 'Saved' : 'Nothing shown';
  return (
    <div className="ink-site-row" style={{ alignItems: 'flex-start' }}>
      <span style={{ flex: 1 }}>
        <span className="ink-site-row-title">{title}</span>
        <span className="ink-site-row-desc" style={{ display: 'block' }}>
          {desc}
        </span>
        <input
          className="ink-site-input"
          style={{ marginTop: 8, width: '100%' }}
          value={text}
          maxLength={200}
          placeholder={placeholder}
          disabled={!canManage}
          onChange={(e) => setText(e.target.value)}
          aria-label={title}
        />
        <span className="ink-site-row-desc" style={{ display: 'block', marginTop: 4 }}>
          {status}
        </span>
      </span>
      <button
        type="button"
        className="ink-btn ink-btn-quiet"
        style={{ marginTop: 28 }}
        disabled={!canManage || saving || text === value}
        onClick={() => onSave(text)}
      >
        Save
      </button>
    </div>
  );
}

export const RETENTION_CHOICES: Array<{ months: number; label: string }> = [
  { months: 1, label: '1 month' },
  { months: 3, label: '3 months' },
  { months: 6, label: '6 months' },
  { months: 12, label: '12 months' },
  { months: 24, label: '2 years' },
  { months: 36, label: '3 years' },
  { months: 0, label: 'Until I delete them' },
];

/** The choices offered (round 27 §1): 3 years only for a site that already has it. */
const retentionChoices = (current: number) =>
  RETENTION_CHOICES.filter((c) => c.months !== 36 || current === 36);

export const retentionText = (months: number) =>
  months === 0 ? 'Kept until you delete them.' : 'Then they’re deleted.';

/** `embedded`: inside Content → Manage, which gives the title (no back link or heading). */
export function CmsFormSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { accountId } = useAccount();
  const { flatNav } = useAddons();
  const { data, loading } = useQuery(GET_TINYCMS_FORM_SETTINGS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const [save, { loading: saving }] = useMutation(UPDATE_TINYCMS_FORM_SETTINGS, {
    refetchQueries: [GET_TINYCMS_FORM_SETTINGS],
  });
  const settings = data?.tinycmsFormSettings;
  const canManage = Boolean(settings?.canManage);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const update = async (input: Record<string, unknown>, done: string) => {
    setMessage(null);
    try {
      await save({ variables: { accountId, input } });
      setMessage({ tone: 'ok', text: done });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Couldn’t save' });
    }
  };

  const changeRetention = async (months: number) => {
    const label = RETENTION_CHOICES.find((c) => c.months === months)?.label;
    if (
      settings &&
      months !== 0 &&
      (settings.retentionMonths === 0 || months < settings.retentionMonths) &&
      !(await askInk({
        title: `Keep responses for ${label}?`,
        body: 'Responses older than that will be deleted and can’t be brought back.',
        confirmLabel: `Keep ${label}`,
        danger: true,
      }))
    )
      return;
    void update({ retentionMonths: months }, 'Saved.');
  };

  return (
    <div className={embedded ? undefined : 'ink-body'}>
      {embedded ? null : (
        <div className="ink-site-settings-head">
          <Link to="/content?view=forms" className="ink-back-link">
            ← Forms
          </Link>
          {flatNav ? (
            <>
              <h1 className="ink-h1">Manage</h1>
              <h2 className="ink-manage-subtitle">Form settings</h2>
            </>
          ) : (
            <h1 className="ink-h1">Form settings</h1>
          )}
          <p className="ink-lede">
            How long responses are kept and what readers see. Applies to every form your agent
            builds.
          </p>
        </div>
      )}
      {loading && !settings ? <Loader label="Loading" className="ink-muted" /> : null}
      {settings ? (
        <>
          <InkSection
            title="Responses"
            description="Every response is kept in Forms, and your agent can’t read them."
          >
            <div className="ink-site-card">
              <div className="ink-site-row">
                <span>
                  <span className="ink-site-row-title">Keep responses for</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    {retentionText(settings.retentionMonths)}
                  </span>
                </span>
                <select
                  className="ink-site-input"
                  aria-label="Keep responses for"
                  style={{ width: 'auto' }}
                  value={settings.retentionMonths}
                  disabled={!canManage || saving}
                  onChange={(e) => void changeRetention(Number(e.target.value))}
                >
                  {retentionChoices(settings.retentionMonths).map((c) => (
                    <option key={c.months} value={c.months}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="ink-site-row">
                <span>
                  <span className="ink-site-row-title">Email me new responses</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    Off unless you turn it on in that form. The email says how many are waiting,
                    never what they say.
                  </span>
                </span>
                <span className="ink-media-rule">Per form</span>
              </div>
            </div>
          </InkSection>
          <InkSection
            title="Protection"
            description="What readers see and agree to when they send a form."
          >
            <div className="ink-site-card">
              <div className="ink-site-row">
                <span>
                  <span className="ink-site-row-title">Spam protection</span>
                  <span className="ink-site-row-desc" style={{ display: 'block' }}>
                    An invisible check. Readers never solve a puzzle.
                  </span>
                </span>
                <span className="ink-media-rule">Always on</span>
              </div>
              <SmallPrintRow
                title="Small print · contact forms"
                desc="Shown under the Send button. Leave empty to show none."
                placeholder="Leave empty to show none"
                value={settings.consent || ''}
                canManage={canManage}
                saving={saving}
                onSave={(consent) =>
                  void update({ consent }, 'Saved. Live forms show it within a minute.')
                }
              />
              <SmallPrintRow
                title="Small print · sign-up forms"
                desc="Shown under every sign-up form. Say what readers get and how to unsubscribe."
                placeholder="What readers get, and how to unsubscribe"
                value={settings.signupConsent || ''}
                canManage={canManage}
                saving={saving}
                onSave={(signupConsent) =>
                  void update({ signupConsent }, 'Saved. Live forms show it within a minute.')
                }
              />
            </div>
          </InkSection>
          {!canManage ? <p className="ink-muted">Only an account admin can change these.</p> : null}
          {message ? (
            <p className={message.tone === 'error' ? 'text-xs text-error' : 'ink-muted'}>
              {message.text}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
